import {
  AuthenticationApi,
  Configuration,
  ErrorMessage400,
  ErrorMessage404,
  OperationIdRequest,
  ReguserRequest,
} from '@ais-ui/api-spec/oceanio';
import { UserCredentials } from '@ais-ui/auth';
import { useMutation, useQuery } from '@tanstack/react-query';

// Development environment
// const APIKEY = 'LVQEGHrM0P5CceeUbGWSO5gX0kZlOG877KrNlDS0';
// const BASEPATH = 'https://api-dev-gui.oceanio.com';

// Demo environment
const APIKEY = 'zGxa4z5NBG5do2VkwRSyQ6EOFmyjnjUH8Qh2ejYU';
const BASEPATH = 'https://api-demo-gui.oceanio.com';

export const config = {
  basePath: BASEPATH,
  headers: {
    'x-api-key': APIKEY as string,
  },
};

export const authEndpoint = new AuthenticationApi(new Configuration(config));

interface AccessToken {
  token: string;
  expires: Date;
}

export const readFromLocalStorage = () => {
  const accessToken = localStorage.getItem('access_token');
  return accessToken != null
    ? (JSON.parse(accessToken) as AccessToken)
    : undefined;
};

export const storeInLocalStorage = (token: AccessToken) => {
  localStorage.setItem('access_token', JSON.stringify(token));
};

export const getAccessToken = (credentials?: UserCredentials) => {
  if (!credentials) {
    return {
      expires: new Date(),
      token: 'invalid token',
    } as AccessToken;
  }

  const request = {
    loginRequest: {
      userId: credentials?.username,
      password: credentials?.password,
    },
  } as OperationIdRequest;

  const accessToken = readFromLocalStorage();

  const now = new Date();
  if (accessToken == null || new Date(accessToken.expires) < now) {
    return authEndpoint
      .operationId(request)
      .then((loginResponse) => {
        const newToken = {
          token: loginResponse.token ?? '',
          expires: new Date(loginResponse.expires ?? now),
        } as AccessToken;
        storeInLocalStorage({
          token: loginResponse.token ?? '',
          expires: new Date(loginResponse.expires ?? now),
        });
        return newToken;
      })
      .catch((error) => {
        return {
          expires: new Date(),
          token: 'failed',
        } as AccessToken;
      })
      .finally(() => {
        return {
          expires: new Date(),
          token: 'failed',
        } as AccessToken;
      });
  } else {
    return accessToken;
  }
};

export const tryLogin = async (credentials: UserCredentials) => {
  const accessToken = await getAccessToken(credentials);

  if (accessToken.token === 'failed') {
    return false;
  }

  return true;
};

export type Response204 = {
  type?: string;
  title?: string;
  errorCode?: number;
  status?: number;
  detail?: string;
  correlationId?: string;
};

export type RegistrationResponse = ErrorMessage400 | Response204 | null;
export type ActivationResponse = ErrorMessage404 | null;

export const useRegistration = () => {
  return useMutation(['registration'], async (request: ReguserRequest) => {
    try {
      const response = await fetch(`${config.basePath}/registration`, {
        method: 'POST',
        body: JSON.stringify({
          first_name: request.registration.firstName,
          last_name: request.registration.lastName,
          company_name: request.registration.companyName,
          email: request.registration.email,
          password: request.registration.password,
          phone_number: request.registration.phoneNumber,
        }),
        headers: {
          'Content-Type': 'application/json',
          ...config.headers,
        },
      });

      return (await response.json()) as RegistrationResponse | null;
    } catch (ex) {
      return null;
    }
  });
};

export const useActivation = () => {
  return useMutation(['activation'], async (activationId: string) => {
    const response = await fetch(
      `${config.basePath}/registration/${activationId}`,
      {
        method: 'POST',
        headers: {
          ...config.headers,
        },
      }
    );

    return (await response.json()) as ActivationResponse | null;
  });
};
